import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { QrCode, FileText, Image as ImageIcon, Wallet, CreditCard, Check, Minus, Plus, ArrowRight, Loader2 } from "lucide-react";

// Auto-cycling 15-second demo of: scan → upload → configure → pay-at-counter → done.
// Pure client-side animation — no external video/hosting needed. Loops forever.

const SCENES = [
  { id: "scan", ms: 3000 },
  { id: "upload", ms: 3000 },
  { id: "configure", ms: 3000 },
  { id: "pay", ms: 3000 },
  { id: "done", ms: 3000 },
];

const CAPTIONS = [
  "1 · Scan the shop's QR",
  "2 · Upload your files",
  "3 · Pick copies & colour",
  "4 · Pay at counter (or online)",
  "5 · Show job # → collect",
];

const fade = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.35 } },
  exit: { opacity: 0, y: -12, transition: { duration: 0.25 } },
};

export default function HeroDemo() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setI((n) => (n + 1) % SCENES.length), SCENES[i].ms);
    return () => clearTimeout(t);
  }, [i]);

  return (
    <div className="relative w-full flex flex-col items-center" data-testid="hero-demo">
      {/* Phone frame */}
      <div className="relative w-[280px] h-[520px] bg-slate-900 rounded-[2.75rem] p-2.5 shadow-2xl ring-1 ring-slate-800/50">
        {/* Notch */}
        <div className="absolute top-2.5 left-1/2 -translate-x-1/2 w-24 h-6 bg-slate-900 rounded-b-2xl z-30" />
        {/* Screen */}
        <div className="relative w-full h-full rounded-[2.1rem] overflow-hidden bg-gradient-to-br from-orange-50 via-white to-amber-50">
          {/* Status bar */}
          <div className="absolute top-0 left-0 right-0 h-8 flex items-center justify-between px-6 text-[10px] font-mono text-slate-500 z-20">
            <span>9:41</span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-1.5 border border-slate-400 rounded-sm relative">
                <span className="absolute inset-0.5 bg-emerald-500 rounded-[1px]" />
              </span>
            </span>
          </div>

          {/* Progress dots */}
          <div className="absolute top-10 left-0 right-0 flex justify-center gap-1.5 z-20">
            {SCENES.map((_, idx) => (
              <div
                key={idx}
                className={`h-1 rounded-full transition-all duration-500 ${
                  idx === i ? "w-6 bg-orange-500" : "w-1.5 bg-slate-300"
                }`}
              />
            ))}
          </div>

          {/* Caption */}
          <div className="absolute bottom-4 left-0 right-0 text-center z-20 px-6">
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">
              Live demo
            </div>
            <AnimatePresence mode="wait">
              <motion.div
                key={CAPTIONS[i]}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="text-[13px] font-semibold text-slate-700 mt-0.5"
              >
                {CAPTIONS[i]}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Scenes */}
          <div className="absolute inset-x-0 top-16 bottom-20 px-5">
            <AnimatePresence mode="wait">
              {i === 0 && <SceneScan key="scan" />}
              {i === 1 && <SceneUpload key="upload" />}
              {i === 2 && <SceneConfigure key="configure" />}
              {i === 3 && <ScenePay key="pay" />}
              {i === 4 && <SceneDone key="done" />}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ----------------------------- Scenes ----------------------------- */

function SceneScan() {
  return (
    <motion.div {...fade} className="h-full flex flex-col items-center justify-center gap-4">
      <div className="text-[10px] font-mono uppercase tracking-widest text-orange-600">Demo Print Shop</div>
      <div className="relative w-40 h-40 rounded-2xl bg-white grid place-items-center shadow-md">
        {/* QR corner brackets */}
        {[
          "top-2 left-2 border-t-2 border-l-2 rounded-tl",
          "top-2 right-2 border-t-2 border-r-2 rounded-tr",
          "bottom-2 left-2 border-b-2 border-l-2 rounded-bl",
          "bottom-2 right-2 border-b-2 border-r-2 rounded-br",
        ].map((c) => (
          <div key={c} className={`absolute w-4 h-4 border-orange-500 ${c}`} />
        ))}
        {/* Fake QR pixels */}
        <div className="grid grid-cols-7 gap-[2px] w-24 h-24">
          {Array.from({ length: 49 }).map((_, k) => {
            const filled = [0,1,2,5,6,7,8,10,12,14,15,17,19,21,22,26,28,29,32,34,35,36,40,41,42,43,45,47,48].includes(k);
            return <div key={k} className={filled ? "bg-slate-900 rounded-[1px]" : "bg-transparent"} />;
          })}
        </div>
        {/* Scan line */}
        <motion.div
          initial={{ y: -60 }}
          animate={{ y: 60 }}
          transition={{ duration: 1.4, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
          className="absolute left-2 right-2 h-0.5 bg-orange-500/70 shadow-[0_0_8px_rgba(249,115,22,0.6)] rounded-full"
        />
      </div>
      <div className="text-center">
        <div className="font-display font-bold text-slate-800">Point camera at QR</div>
        <div className="text-[11px] text-slate-500 mt-0.5">No app · works from any browser</div>
      </div>
    </motion.div>
  );
}

function SceneUpload() {
  const files = [
    { name: "Resume.pdf", pages: "3 pages", Icon: FileText, delay: 0.1 },
    { name: "Photo.jpg", pages: "1 page", Icon: ImageIcon, delay: 0.5 },
  ];
  return (
    <motion.div {...fade} className="h-full flex flex-col gap-3 pt-2">
      <div className="text-[10px] font-mono uppercase tracking-widest text-orange-600">Upload</div>
      {files.map((f) => (
        <motion.div
          key={f.name}
          initial={{ opacity: 0, x: -14 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: f.delay, duration: 0.3 }}
          className="rounded-xl bg-white border border-slate-200 p-3 shadow-sm"
        >
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-orange-100 grid place-items-center flex-shrink-0">
              <f.Icon className="w-4 h-4 text-orange-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-slate-800 truncate">{f.name}</div>
              <div className="text-[10px] text-slate-500 font-mono">{f.pages}</div>
            </div>
            <Check className="w-4 h-4 text-emerald-500" />
          </div>
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: "100%" }}
            transition={{ delay: f.delay + 0.2, duration: 0.8 }}
            className="mt-2 h-1 bg-emerald-500 rounded-full"
          />
        </motion.div>
      ))}
      <div className="mt-auto rounded-xl border-2 border-dashed border-slate-300 p-3 text-center">
        <Loader2 className="w-4 h-4 mx-auto text-slate-400 animate-spin" />
        <div className="text-[10px] text-slate-400 mt-1">Adding more…</div>
      </div>
    </motion.div>
  );
}

function SceneConfigure() {
  return (
    <motion.div {...fade} className="h-full flex flex-col gap-3 pt-2">
      <div className="text-[10px] font-mono uppercase tracking-widest text-orange-600">Options</div>
      <div className="rounded-xl bg-white border border-slate-200 p-3 shadow-sm">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-orange-100 grid place-items-center flex-shrink-0">
            <FileText className="w-4 h-4 text-orange-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold text-slate-800 truncate">Resume.pdf</div>
            <div className="text-[10px] text-slate-500 font-mono">3 pages · ₹2/page</div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button className="w-6 h-6 rounded-md bg-slate-100 grid place-items-center"><Minus className="w-3 h-3" /></button>
            <motion.span
              key="copies"
              initial={{ scale: 0.5 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.6, type: "spring", stiffness: 400 }}
              className="w-5 text-center font-mono font-bold text-xs"
            >2</motion.span>
            <button className="w-6 h-6 rounded-md bg-orange-100 grid place-items-center"><Plus className="w-3 h-3 text-orange-600" /></button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-1 mt-2.5">
          <motion.div initial={{ opacity: 0.3 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="py-1 rounded-md text-[10px] font-semibold text-orange-700 bg-orange-50 border border-orange-200 text-center">B&W</motion.div>
          <motion.div initial={{ opacity: 0.3 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="py-1 rounded-md text-[10px] font-semibold text-orange-700 bg-orange-50 border border-orange-200 text-center">Portrait</motion.div>
        </div>
      </div>
      <div className="mt-auto rounded-xl bg-orange-50 border border-orange-200 p-3 flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-700">Running total</span>
        <motion.span
          key="total"
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.3 }}
          className="font-mono font-bold text-orange-600 text-lg"
        >₹12</motion.span>
      </div>
    </motion.div>
  );
}

function ScenePay() {
  return (
    <motion.div {...fade} className="h-full flex flex-col gap-2.5 pt-2">
      <div className="text-[10px] font-mono uppercase tracking-widest text-orange-600">Payment method</div>
      {/* Cash tile — highlighted */}
      <motion.div
        initial={{ scale: 0.94, borderColor: "#e2e8f0" }}
        animate={{ scale: 1, borderColor: "#F97316", backgroundColor: "#FFF7ED" }}
        transition={{ delay: 0.6, duration: 0.4 }}
        className="rounded-xl border-2 p-3 relative"
      >
        <div className="flex items-center gap-2 mb-0.5">
          <Wallet className="w-4 h-4 text-orange-600" />
          <div className="text-xs font-bold text-slate-900">Pay at counter</div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9 }}
            className="ml-auto w-4 h-4 rounded-full bg-orange-500 grid place-items-center"
          >
            <Check className="w-2.5 h-2.5 text-white" />
          </motion.div>
        </div>
        <div className="text-[10px] text-slate-500">Cash to shop · prints after approval</div>
      </motion.div>
      {/* Online tile */}
      <div className="rounded-xl border border-slate-200 bg-white p-3">
        <div className="flex items-center gap-2 mb-0.5">
          <CreditCard className="w-4 h-4 text-slate-400" />
          <div className="text-xs font-semibold text-slate-500">Pay online</div>
        </div>
        <div className="text-[10px] text-slate-400">UPI, cards, netbanking</div>
      </div>
      <motion.button
        initial={{ opacity: 0.6 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="mt-auto h-10 rounded-xl bg-orange-500 text-white text-xs font-bold flex items-center justify-center gap-2 shadow-md"
      >
        <Wallet className="w-3.5 h-3.5" /> Place cash order <ArrowRight className="w-3.5 h-3.5" />
      </motion.button>
    </motion.div>
  );
}

function SceneDone() {
  return (
    <motion.div {...fade} className="h-full flex flex-col items-center justify-center gap-3 text-center">
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 260, damping: 18 }}
        className="w-16 h-16 rounded-full bg-emerald-500 grid place-items-center shadow-lg shadow-emerald-500/30"
      >
        <Check className="w-9 h-9 text-white" strokeWidth={3} />
      </motion.div>
      <div>
        <div className="font-display font-bold text-slate-800">Order placed!</div>
        <div className="text-[11px] text-slate-500 mt-0.5">Show this at the counter</div>
      </div>
      <div className="w-full rounded-xl bg-white border border-slate-200 p-3 shadow-sm">
        <div className="text-[9px] font-mono uppercase tracking-widest text-slate-400">Job number</div>
        <div className="font-mono font-bold text-slate-900 text-base">CASH-20260220-A7B4</div>
        <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between">
          <span className="text-[11px] text-slate-500">Pay at counter</span>
          <span className="font-mono font-bold text-orange-600">₹12.00</span>
        </div>
      </div>
    </motion.div>
  );
}
